import { useEffect, useRef } from 'react';
import { FileDisplay } from '../components/FileToDisplay/FileToDisplay.types';

interface UseFileProgressPollingParams {
  isActive: boolean;
  entityId: number | null | undefined;
  filesFromStorage: FileDisplay[];
  updateFilesCallback: (files: FileDisplay[]) => void;
  fetchFunction: (...args: any[]) => Promise<{ files_from_storage: FileDisplay[] }>;
  fetchParams: any[];
}

/**
 * Хук для автоматического обновления прогресса файлов со статусом "processing"
 * Обновляет данные каждые 5 секунд, пока есть файлы в обработке и компонент активен
 */
export const useFileProgressPolling = ({
  isActive,
  entityId,
  filesFromStorage,
  updateFilesCallback,
  fetchFunction,
  fetchParams,
}: UseFileProgressPollingParams) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const fetchParamsRef = useRef<any[]>(fetchParams);
  const filesFromStorageRef = useRef<FileDisplay[]>(filesFromStorage);
  const updateCallbackRef = useRef(updateFilesCallback);
  const fetchFunctionRef = useRef(fetchFunction);

  // Обновляем refs при изменении параметров
  useEffect(() => {
    fetchParamsRef.current = fetchParams;
    filesFromStorageRef.current = filesFromStorage;
    updateCallbackRef.current = updateFilesCallback;
    fetchFunctionRef.current = fetchFunction;
  }, [fetchParams, filesFromStorage, updateFilesCallback, fetchFunction]);

  useEffect(() => {
    // Всегда очищаем предыдущий интервал перед созданием нового
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Обновляем ref для использования внутри интервала
    filesFromStorageRef.current = filesFromStorage;

    // Проверяем, есть ли файлы в обработке (используем актуальные данные)
    const hasProcessingFiles = filesFromStorage.some(
      (file) => file.type === 'video' && file.status === 'processing'
    );

    // Если компонент активен, есть ID и есть файлы в обработке - запускаем обновление
    if (isActive && entityId && hasProcessingFiles) {
      const fetchAndUpdate = async () => {
        try {
          const response = await fetchFunctionRef.current(...fetchParamsRef.current);
          
          if (response && response.files_from_storage) {
            // Обновляем ref перед вызовом колбэка, чтобы следующая проверка использовала актуальные данные
            filesFromStorageRef.current = response.files_from_storage;
            updateCallbackRef.current(response.files_from_storage);
          }
        } catch (error) {
          console.error('Ошибка при обновлении прогресса файлов:', error);
        }
      };

      // Первый запрос сразу
      fetchAndUpdate();

      // Затем каждые 5 секунд
      intervalRef.current = setInterval(() => {
        // Проверяем снова перед каждым запросом, есть ли еще файлы в обработке
        const stillHasProcessing = filesFromStorageRef.current.some(
          (file) => file.type === 'video' && file.status === 'processing'
        );
        if (stillHasProcessing) {
          fetchAndUpdate();
        } else {
          // Если больше нет файлов в обработке, останавливаем интервал
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, 5000);
    }

    // Очистка при размонтировании или изменении зависимостей
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // Убираем filesFromStorage из зависимостей, чтобы не перезапускать эффект при каждом обновлении
    // Проверка наличия файлов в обработке происходит внутри интервала
  }, [isActive, entityId]);

  // Отдельный эффект для проверки исчезновения файлов в обработке
  // (чтобы остановить интервал, если файлы закончили обработку)
  useEffect(() => {
    const hasProcessingFiles = filesFromStorage.some(
      (file) => file.type === 'video' && file.status === 'processing'
    );

    // Если файлов в обработке больше нет, но интервал все еще работает - останавливаем его
    if (!hasProcessingFiles && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [filesFromStorage]);
};

